import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Flex, Icon, Input, Menu, MenuButton, MenuItem, MenuList, Text } from "@chakra-ui/react";
import { HiMinus, HiPlus, HiOutlineArrowPath } from "react-icons/hi2";
import { c, PALETTES } from "../../classroomColors";
import { STANDE } from "../../standeTokens";
import { defaultRoomName, firstName, movePerson, roomMark, splitIntoRooms } from "./roomPlan";

const MAX_ROOMS = 12;

const solid = {
  bg: c.solidBg,
  color: c.solidText,
  _hover: { bg: c.solidBg, opacity: 0.86 },
  _active: { bg: c.solidBg, opacity: 0.8 },
};

const quiet = {
  bg: c.surface,
  color: c.text,
  border: "1px solid",
  borderColor: c.borderSoft,
  _hover: { bg: c.hover },
};

/** One student in the planner: dragged to another room, or moved from its menu. */
const PersonChip = ({ person, rooms, fromIndex, onMove, isDark, away }) => {
  const p = isDark ? PALETTES.dark : PALETTES.light;
  return (
    <Menu isLazy placement="bottom-start">
      <MenuButton
        as={Box}
        role="button"
        tabIndex={0}
        draggable
        onDragStart={(e) => {
          e.dataTransfer.setData("text/stande-person", person.guid);
          e.dataTransfer.effectAllowed = "move";
        }}
        display="inline-flex"
        alignItems="center"
        gap="5px"
        px="9px"
        h="26px"
        borderRadius="200px"
        bg={c.surface}
        border="1px solid"
        borderColor={c.borderSoft}
        fontSize="12px"
        color={away ? c.textMuted : c.text}
        borderStyle={away ? "dashed" : "solid"}
        cursor="grab"
        _hover={{ borderColor: c.textMuted }}
        title={away ? `${person.name} has left the session` : undefined}
        aria-label={`${person.name}${away ? ", has left the session" : ""}. Move to another room`}
      >
        {firstName(person.name)}
        {away && <Box as="span" fontSize="10.5px">· left</Box>}
      </MenuButton>
      {/* Portalled: the literal palette, as the variables do not reach it. */}
      <MenuList minW="160px" py="4px" bg={p.surface} borderColor={p.border} fontSize="12.5px" zIndex={40}>
        <Text px="12px" py="4px" fontSize="11px" color={p.textMuted}>
          Move {firstName(person.name)} to
        </Text>
        {rooms.map((room, i) =>
          i === fromIndex ? null : (
            <MenuItem key={i} bg={p.surface} color={p.text} _hover={{ bg: p.hover }} onClick={() => onMove(person.guid, i)}>
              <Box boxSize="7px" borderRadius="full" bg={roomMark(i)} mr="8px" />
              {room.name || `Room ${i + 1}`}
            </MenuItem>
          )
        )}
        {fromIndex >= 0 && (
          <MenuItem bg={p.surface} color={p.text} _hover={{ bg: p.hover }} onClick={() => onMove(person.guid, null)}>
            <Box boxSize="7px" borderRadius="full" bg={p.textMuted} mr="8px" />
            Back to the class
          </MenuItem>
        )}
      </MenuList>
    </Menu>
  );
};

/** Rooms as the server has them, as the editor holds them. */
const fromServer = (rooms) => rooms.map((r) => ({ id: r.id, name: r.name, members: r.members }));

/** Same people in the same rooms with the same names? (Order of people aside.) */
const sameArrangement = (a, b) =>
  a.length === b.length &&
  a.every((r, i) => {
    const o = b[i];
    if (r.id !== o.id || r.name.trim() !== o.name.trim()) return false;
    const x = r.members.map((m) => m.guid).sort().join();
    const y = o.members.map((m) => m.guid).sort().join();
    return x === y;
  });

/**
 * Sub-rooms, in the room's dock (the teacher's view).
 *
 * One editable arrangement, before the rooms open and after. Rooms are for
 * the students in the session right now — not everyone in the space — so
 * only they are dealt in, and only they wait to be placed (the server
 * holds to the same rule). Someone placed who then leaves stays in their
 * room, marked as gone, until the teacher moves them. Before: choose how
 * many, and the people here are dealt in evenly. After: the same rooms, as they are, still editable —
 * drag a student's chip to another room or move them from its menu (back
 * to the class too), add or take away a room, rename, shuffle — and Apply
 * sends the change to everyone at once. Rooms that stay keep their boards.
 * Each open room has its way in; the class is a room like the others.
 */
const RoomsPanel = ({ roster, subRooms, viewRoomId, onJoin, isDark }) => {
  const here = useMemo(() => roster.filter((s) => s.isPresent), [roster]);
  const hereGuids = useMemo(() => new Set(here.map((s) => s.guid)), [here]);
  const [count, setCount] = useState(Math.min(4, Math.max(2, Math.ceil((here.length || 6) / 4))));
  // The arrangement being edited. Before opening, it follows the choices
  // until the teacher touches it; once open, it follows the server until
  // the teacher touches it.
  const [draft, setDraft] = useState(null);
  const [touched, setTouched] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [dropTarget, setDropTarget] = useState(null);
  const isOpen = subRooms.isOpen;

  useEffect(() => {
    if (touched) return;
    if (isOpen) setDraft(fromServer(subRooms.rooms));
    else setDraft(splitIntoRooms(here.map((s) => ({ guid: s.guid, name: s.name })), count));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, subRooms.rooms, count, here.length, touched]);

  const rooms = draft || [];
  const edit = (next) => {
    setDraft(next);
    setTouched(true);
  };
  const dirty = isOpen && touched && !sameArrangement(rooms, fromServer(subRooms.rooms));

  const placed = new Set(rooms.flatMap((r) => r.members.map((m) => m.guid)));
  const waiting = here.filter((s) => !placed.has(s.guid));

  const addRoom = () => {
    if (!isOpen && !touched) return setCount((n) => Math.min(MAX_ROOMS, n + 1));
    edit([...rooms, { id: null, name: defaultRoomName(rooms.length), members: [] }]);
  };
  const removeRoom = () => {
    if (!isOpen && !touched) return setCount((n) => Math.max(1, n - 1));
    if (rooms.length <= 1) return;
    // The last room goes; whoever was in it waits with the class.
    edit(rooms.slice(0, -1));
  };
  // Re-deal: everyone placed, and whoever is here and waiting, evenly across
  // the rooms as they stand — names and boards kept.
  const shuffle = () => {
    const people = [
      ...rooms.flatMap((r) => r.members),
      ...waiting.map((s) => ({ guid: s.guid, name: s.name })),
    ];
    const dealt = splitIntoRooms(people, rooms.length, { shuffle: true });
    edit(rooms.map((r, i) => ({ ...r, members: dealt[i].members })));
  };
  const move = (guid, to) => {
    const person = waiting.find((s) => s.guid === guid);
    if (person) {
      if (to == null) return;
      edit(rooms.map((r, i) => (i === to ? { ...r, members: [...r.members, { guid: person.guid, name: person.name }] } : r)));
    } else {
      edit(movePerson(rooms, guid, to));
    }
  };

  const submit = async () => {
    const ok = await subRooms.open(
      rooms.map((r) => ({
        id: r.id,
        name: r.name.trim() || "Room",
        memberGuids: r.members.map((m) => m.guid),
        members: r.members,
      }))
    );
    if (ok) setTouched(false);
  };

  const roomCount = rooms.length;

  return (
    <Flex direction="column" minH={0} flex="1 1 auto">
      <Flex direction="column" gap="10px" px="14px" pb="10px">
        <Text fontSize="12px" color={c.textSub} lineHeight="1.45">
          {isOpen
            ? "Rooms are open. Move people, add or take away a room, then apply — rooms that stay keep their boards."
            : here.length
            ? `${here.length === 1 ? "1 student is" : `${here.length} students are`} in the session. Split them into smaller rooms: each gets its own board, and you can drop into any of them.`
            : "No students are in the session yet. Rooms are made from the people here, so wait for them to join."}
        </Text>
        {isOpen && (
          <RoomRow mark={c.textSub} name="The class" detail="Everyone not in a room" isHere={!viewRoomId} onJoin={() => onJoin(null)} />
        )}
        {(isOpen || here.length > 0) && (
        <Flex align="center" justify="space-between">
          <Text fontSize="12.5px" color={c.text} fontWeight="500">Rooms</Text>
          <Flex align="center" gap="4px">
            <Stepper label="Take away the last room" icon={HiMinus} isDisabled={roomCount <= 1} onClick={removeRoom} />
            <Text w="26px" textAlign="center" fontSize="13px" fontWeight="600" color={c.text} sx={{ fontVariantNumeric: "tabular-nums" }}>
              {roomCount}
            </Text>
            <Stepper label="Add a room" icon={HiPlus} isDisabled={roomCount >= MAX_ROOMS} onClick={addRoom} />
            <Stepper label="Shuffle who goes where" icon={HiOutlineArrowPath} onClick={shuffle} />
          </Flex>
        </Flex>
        )}
      </Flex>

      <Flex direction="column" gap="8px" px="14px" pb="10px" flex="1 1 auto" minH={0} overflowY="auto" data-own-scroll>
        {(isOpen || here.length > 0) && rooms.map((room, i) => {
          const isHere = isOpen && room.id && viewRoomId === room.id;
          return (
            <Box
              key={room.id || `new-${i}`}
              p="10px"
              borderRadius="12px"
              bg={dropTarget === i || isHere ? c.surface : c.glassEdge}
              border="1px solid"
              borderColor={dropTarget === i ? roomMark(i) : isHere ? STANDE.primaryBlue : c.borderSoft}
              transition="border-color 0.12s, background 0.12s"
              onDragOver={(e) => {
                e.preventDefault();
                setDropTarget(i);
              }}
              onDragLeave={() => setDropTarget((t) => (t === i ? null : t))}
              onDrop={(e) => {
                e.preventDefault();
                setDropTarget(null);
                const guid = e.dataTransfer.getData("text/stande-person");
                if (guid) move(guid, i);
              }}
            >
              <Flex align="center" gap="8px" mb="8px">
                <Box boxSize="8px" borderRadius="full" bg={roomMark(i)} flexShrink={0} />
                <Input
                  value={room.name}
                  onChange={(e) => edit(rooms.map((r, j) => (j === i ? { ...r, name: e.target.value } : r)))}
                  aria-label={`Name of room ${i + 1}`}
                  variant="unstyled"
                  fontSize="13px"
                  fontWeight="500"
                  color={c.text}
                  h="22px"
                />
                <Text fontSize="11px" color={c.textMuted} flexShrink={0}>{room.members.length}</Text>
                {isOpen && room.id && (
                  isHere ? (
                    <Text fontSize="11px" color={c.textSub} flexShrink={0}>You're here</Text>
                  ) : (
                    <Button size="xs" h="24px" px="10px" borderRadius="8px" fontSize="11.5px" fontWeight="500" flexShrink={0}
                      {...solid} onClick={() => onJoin(room.id)}>
                      Join
                    </Button>
                  )
                )}
              </Flex>
              <Flex wrap="wrap" gap="5px" minH="26px">
                {room.members.length === 0 && (
                  <Text fontSize="11.5px" color={c.textMuted} py="4px">Drag someone here</Text>
                )}
                {room.members.map((m) => (
                  <PersonChip key={m.guid} person={m} rooms={rooms} fromIndex={i} isDark={isDark} onMove={move} away={!hereGuids.has(m.guid)} />
                ))}
              </Flex>
            </Box>
          );
        })}

        {waiting.length > 0 && (
          <Box
            p="10px"
            borderRadius="12px"
            border="1px dashed"
            borderColor={dropTarget === "class" ? c.textMuted : c.borderSoft}
            onDragOver={(e) => {
              e.preventDefault();
              setDropTarget("class");
            }}
            onDragLeave={() => setDropTarget((t) => (t === "class" ? null : t))}
            onDrop={(e) => {
              e.preventDefault();
              setDropTarget(null);
              const guid = e.dataTransfer.getData("text/stande-person");
              if (guid) move(guid, null);
            }}
          >
            <Text fontSize="11.5px" color={c.textSub} mb="6px">Here, not in a room · they stay with the class</Text>
            <Flex wrap="wrap" gap="5px">
              {waiting.map((s) => (
                <PersonChip key={s.guid} person={{ guid: s.guid, name: s.name }} rooms={rooms} fromIndex={-1} isDark={isDark} onMove={move} />
              ))}
            </Flex>
          </Box>
        )}
      </Flex>

      <Box px="14px" pb="14px" pt="4px">
        {subRooms.error && (
          <Text fontSize="12px" color={c.accentSoftText} mb="8px" role="alert">{subRooms.error}</Text>
        )}
        {!isOpen ? (
          <Button {...solid} w="100%" size="sm" h="38px" borderRadius="12px" fontSize="13px" fontWeight="500"
            isLoading={subRooms.busy}
            isDisabled={!rooms.length || rooms.every((r) => !r.members.length)}
            onClick={submit}>
            {`Open ${rooms.length} ${rooms.length === 1 ? "room" : "rooms"}`}
          </Button>
        ) : confirmClose ? (
          <Box p="12px" borderRadius="12px" bg={c.surface} border="1px solid" borderColor={c.borderSoft}>
            <Text fontSize="12.5px" color={c.text} fontWeight="500">Bring everyone back?</Text>
            <Text fontSize="12px" color={c.textSub} mt="2px" lineHeight="1.45">
              Every room closes and its board is cleared. Anything worth keeping, bring to the class board first.
            </Text>
            <Flex gap="8px" mt="10px">
              <Button {...solid} size="sm" h="32px" borderRadius="10px" fontSize="12.5px" fontWeight="500" flex="1"
                isLoading={subRooms.busy}
                onClick={async () => {
                  if (await subRooms.close()) {
                    setConfirmClose(false);
                    setTouched(false);
                    onJoin(null);
                  }
                }}>
                Close every room
              </Button>
              <Button {...quiet} size="sm" h="32px" borderRadius="10px" fontSize="12.5px" fontWeight="500"
                onClick={() => setConfirmClose(false)}>
                Keep them
              </Button>
            </Flex>
          </Box>
        ) : (
          <Flex gap="8px">
            {dirty ? (
              <>
                <Button {...quiet} size="sm" h="38px" borderRadius="12px" fontSize="13px" fontWeight="500"
                  onClick={() => setTouched(false)}>
                  Undo changes
                </Button>
                <Button {...solid} flex="1" size="sm" h="38px" borderRadius="12px" fontSize="13px" fontWeight="500"
                  isLoading={subRooms.busy} isDisabled={!rooms.length} onClick={submit}>
                  Apply changes
                </Button>
              </>
            ) : (
              <Button {...quiet} flex="1" size="sm" h="38px" borderRadius="12px" fontSize="13px" fontWeight="500"
                onClick={() => setConfirmClose(true)}>
                Close rooms
              </Button>
            )}
          </Flex>
        )}
      </Box>
    </Flex>
  );
};

const Stepper = ({ label, icon, onClick, isDisabled }) => (
  <Flex
    as="button"
    type="button"
    aria-label={label}
    title={label}
    onClick={onClick}
    disabled={isDisabled}
    boxSize="26px"
    align="center"
    justify="center"
    borderRadius="8px"
    bg={c.surface}
    border="1px solid"
    borderColor={c.borderSoft}
    color={isDisabled ? c.textMuted : c.text}
    opacity={isDisabled ? 0.5 : 1}
    _hover={isDisabled ? {} : { bg: c.hover }}
  >
    <Icon as={icon} boxSize="13px" />
  </Flex>
);

const RoomRow = ({ mark, name, detail, count, isHere, onJoin }) => (
  <Flex
    align="center"
    gap="10px"
    p="10px"
    borderRadius="12px"
    bg={isHere ? c.surface : c.glassEdge}
    border="1px solid"
    borderColor={isHere ? STANDE.primaryBlue : c.borderSoft}
  >
    <Box boxSize="8px" borderRadius="full" bg={mark} flexShrink={0} />
    <Box flex="1" minW={0}>
      <Text fontSize="13px" fontWeight="500" color={c.text} noOfLines={1}>
        {name}
        {count != null && (
          <Box as="span" ml="6px" fontSize="11px" fontWeight="400" color={c.textMuted}>{count}</Box>
        )}
      </Text>
      <Text fontSize="11.5px" color={c.textSub} noOfLines={1}>{detail}</Text>
    </Box>
    {isHere ? (
      <Text fontSize="11.5px" color={c.textSub} flexShrink={0}>You're here</Text>
    ) : (
      <Button size="xs" h="28px" px="12px" borderRadius="9px" fontSize="12px" fontWeight="500" flexShrink={0}
        bg={c.solidBg} color={c.solidText} _hover={{ bg: c.solidBg, opacity: 0.86 }} onClick={onJoin}>
        Join
      </Button>
    )}
  </Flex>
);

export default RoomsPanel;
